import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { useMe } from '~/api/auth';
import {
  CLOCK_STATE_LABEL,
  type ClockState,
  clockDaysQueryOptions,
  liveWorked,
  teamClockQueryOptions,
} from '~/api/clock';
import { useServerClock } from '~/api/serverClock';
import { ClockCard } from '~/features/clock/ClockCard';
import { DayPlaceDialog, DayPlacePills } from '~/features/clock/DayPlaceDialog';
import { DaysTable } from '~/features/clock/DaysTable';
import { WorkTimeline } from '~/features/clock/WorkTimeline';
import { TeamHourly } from '~/features/hourly/TeamHourly';
import { VerifyDayDialog } from '~/features/clock/VerifyDayDialog';
import {
  TEAM_SORTS,
  TEAM_STATUSES,
  type TeamPerson,
  type TeamSort,
  type TeamStatus,
  hasStatus,
  isMissing,
  narrow,
  sortTeam,
} from '~/features/clock/teamFilter';
import { departmentsQueryOptions } from '~/api/people';
import { Input } from '~/ui/Input';
import { Select } from '~/ui/Select';
import { formatClock, formatMinutes, formatTime } from '~/lib/format';
import { Avatar } from '~/ui/Avatar';
import { Card } from '~/ui/Card';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { PageHeader } from '~/ui/PageHeader';
import { Pill } from '~/ui/Pill';
import { Skeleton } from '~/ui/Skeleton';
import { Tabs } from '~/ui/Tabs';
import type { Tone } from '~/ui/tones';

type View = 'mine' | 'team' | 'reports';
// `reports` is kept so old links land on the Reports page, where day reports now live.

export const Route = createFileRoute('/_app/clock')({
  validateSearch: (s: Record<string, unknown>): { view?: View } => ({
    view: s['view'] === 'team' || s['view'] === 'reports' ? s['view'] : undefined,
  }),
  component: ClockPage,
});

const STATE_TONE: Record<ClockState, Tone> = {
  working: 'done',
  break: 'warn',
  out: 'neutral',
  not_started: 'neutral',
};

function ClockPage() {
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/clock' });
  const canSeeTeam = me.person.role !== 'member';
  const view: View = canSeeTeam && search.view ? search.view : 'mine';
  if (view === 'reports') return <Navigate to="/reports" search={{ view: 'team' }} replace />;

  return (
    <div className="mx-auto max-w-[1240px]">
      <PageHeader
        eyebrow="Work"
        title="Work clock"
        subtitle="When work starts and stops, with breaks. Separate from the office punch, which says when someone arrived."
      />
      <div className="mb-4">
        <ClockCard />
      </div>
      {canSeeTeam && (
        <div className="mb-4" data-tour="clock-view">
          <Tabs
            label="Whose days"
            value={view}
            onChange={(v) =>
              void navigate({ search: { view: v === 'mine' ? undefined : v }, replace: true })
            }
            options={[
              { value: 'mine', label: 'My days' },
              { value: 'team', label: 'Team today' },
            ]}
          />
        </div>
      )}
      {view === 'team' ? (
        <TeamToday />
      ) : (
        <div className="flex flex-col gap-4">
          <div data-tour="clock-timeline">
            <WorkTimeline />
          </div>
          <MyDays />
        </div>
      )}
    </div>
  );
}

function MyDays() {
  const me = useMe();
  const now = useServerClock(1000);
  const days = useQuery(clockDaysQueryOptions());
  return (
    <Card title="My days" eyebrow="The last two weeks">
      {days.isPending ? (
        <Skeleton lines={5} />
      ) : days.isError ? (
        <ErrorNotice error={days.error} onRetry={() => days.refetch()} />
      ) : (
        <DaysTable
          days={days.data.days}
          tz={me.person.tz ?? me.workspace.tz}
          measuredAt={days.data.measuredAt}
          now={now}
        />
      )}
    </Card>
  );
}

function TeamToday() {
  const me = useMe();
  const tz = me.workspace.tz;
  const now = useServerClock(1000);
  const team = useQuery(teamClockQueryOptions());
  const departments = useQuery(departmentsQueryOptions);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [verifyId, setVerifyId] = useState<string | null>(null);
  const [status, setStatus] = useState<TeamStatus>('all');
  const [q, setQ] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [sort, setSort] = useState<TeamSort>('name');
  if (team.isPending) return <Skeleton lines={6} />;
  if (team.isError) return <ErrorNotice error={team.error} onRetry={() => team.refetch()} />;
  const t = team.data;
  const worked = (p: TeamPerson) => liveWorked(p.day, t.measuredAt, now);
  // Search and department narrow the list; the status buttons count what is left.
  const narrowed = narrow(t.people, { q, departmentId });
  const shown = sortTeam(
    narrowed.filter((p) => hasStatus(p, status)),
    sort,
    worked,
  );
  const missing = narrowed.filter(isMissing).length;
  // A manager does not review their own day; the owner may.
  const waiting = t.people.flatMap((p) =>
    p.day?.review === 'pending' && (p.personId !== me.person.id || me.person.role === 'owner')
      ? [p.day]
      : [],
  );
  const reviewing = waiting.find((d) => d.id === reviewId) ?? null;
  // Clock-ins the desktop app sent later, after an offline morning: the same rule.
  const sentLater = t.people.flatMap((p) =>
    p.day?.unverified && (p.personId !== me.person.id || me.person.role === 'owner') ? [p.day] : [],
  );
  const verifying = sentLater.find((d) => d.id === verifyId) ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        {waiting.length > 0 && <Pill tone="warn">{waiting.length} to review</Pill>}
        {sentLater.length > 0 && <Pill tone="warn">{sentLater.length} sent later</Pill>}
        {missing > 0 && <Pill tone="late">{missing} not started</Pill>}
      </div>
      {sentLater.length > 0 && (
        <Card title="Clock-ins sent later" eyebrow="Waiting to be verified">
          <ul className="divide-y divide-line">
            {sentLater.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <Avatar name={d.personName} size="sm" personId={d.personId} />
                <span className="min-w-[140px] flex-1 font-medium">{d.personName}</span>
                <span className="text-xs text-ink-3">
                  in {formatTime(d.clientClaimedAt ?? d.clockedInAt, tz)} by their computer
                  {d.replayedAt && <>, arrived {formatTime(d.replayedAt, tz)}</>}
                </span>
                <button
                  type="button"
                  onClick={() => setVerifyId(d.id)}
                  className="h-8 rounded-control border border-line px-3 text-sm hover:bg-paper-2"
                >
                  Verify
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <VerifyDayDialog day={verifying} tz={tz} onClose={() => setVerifyId(null)} />
      {waiting.length > 0 && (
        <Card title="Clocked in away from the office" eyebrow="Waiting for review">
          <ul className="divide-y divide-line">
            {waiting.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <Avatar name={d.personName} size="sm" personId={d.personId} />
                <span className="min-w-[140px] flex-1 font-medium">{d.personName}</span>
                <span className="text-xs text-ink-3">in {formatTime(d.clockedInAt, tz)}</span>
                <DayPlacePills day={d} />
                <button
                  type="button"
                  onClick={() => setReviewId(d.id)}
                  className="h-8 rounded-control border border-line px-3 text-sm hover:bg-paper-2"
                >
                  Review
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <DayPlaceDialog day={reviewing} tz={tz} canReview onClose={() => setReviewId(null)} />
      <TeamHourly />
      <Card padded={false}>
        <div className="flex flex-col gap-3 border-b border-line px-5 py-4">
          <div role="group" aria-label="Show" className="flex flex-wrap gap-1.5">
            {TEAM_STATUSES.map((s) => {
              const n = narrowed.filter((p) => hasStatus(p, s.value)).length;
              const on = status === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setStatus(s.value)}
                  className={`h-8 rounded-full border px-3 text-sm transition-colors ${
                    on
                      ? 'border-accent bg-accent text-on-accent'
                      : 'border-line bg-paper text-ink-2 hover:bg-paper-2'
                  }`}
                >
                  {s.label} <span className="font-mono tabular opacity-80">{n}</span>
                </button>
              );
            })}
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label="Search"
              type="search"
              placeholder="Name or title"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {(departments.data?.length ?? 0) > 0 && (
              <Select
                label="Department"
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
              >
                <option value="">Every department</option>
                {departments.data?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
            )}
            <Select
              label="Sort by"
              value={sort}
              onChange={(e) => setSort(e.target.value as TeamSort)}
            >
              {TEAM_SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
        {shown.length === 0 && (
          <p className="px-5 py-6 text-sm text-ink-3">Nobody matches. Try another filter.</p>
        )}
        <ul className="divide-y divide-line">
          {shown.map((p) => (
            <li key={p.personId}>
              <Link
                to="/people/$id"
                params={{ id: p.personId }}
                className="flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-paper-2/50"
              >
                <Avatar name={p.name} size="sm" personId={p.personId} />
                <div className="min-w-[140px] flex-1">
                  <p className="text-sm font-medium">{p.name}</p>
                  {p.title && <p className="text-xs text-ink-3">{p.title}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
                  {p.day && <span>in {formatTime(p.day.clockedInAt, tz)}</span>}
                  {p.day?.clockedOutAt && <span>out {formatTime(p.day.clockedOutAt, tz)}</span>}
                  {p.day && <span className="font-mono tabular">{formatClock(worked(p))}</span>}
                  {(p.day?.lateMinutes ?? 0) > 0 && (
                    <Pill tone="late">{formatMinutes(p.day!.lateMinutes!)} late</Pill>
                  )}
                  {p.day?.lateNote && <span className="text-ink-2">“{p.day.lateNote}”</span>}
                  {p.day && p.day.checks.length > 0 && <DayPlacePills day={p.day} />}
                  {!p.scheduled && !p.day ? (
                    <Pill>Day off</Pill>
                  ) : (
                    <Pill
                      tone={p.state === 'not_started' && p.scheduled ? 'late' : STATE_TONE[p.state]}
                    >
                      {CLOCK_STATE_LABEL[p.state]}
                    </Pill>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
